import { normalizeTier, type NormalizedTier, type RawTier } from './tiers';

export interface RateBounds {
  min: number;
  max: number;
}

/**
 * Alli policy: sliding-scale rate range per therapist tier (CAD/session).
 * These bounds are the single source of truth for:
 *   - the matching-quiz rate slider
 *   - the booking-confirm rate validator
 *   - the self-pay rate picker (imwell + mhap wallet-exhausted path)
 *   - the therapist book-on-behalf rate picker
 */
export const TIER_RATE_BOUNDS: Record<NormalizedTier, RateBounds> = {
  resident: { min: 45, max: 115 },
  associate: { min: 125, max: 200 },
};

// Grandfather: rates valid under the previous policy (pre-2026-10-07). Returning
// clients whose stored episode/quiz rate predates the policy change keep booking
// at their legacy rate; new rate picks must satisfy TIER_RATE_BOUNDS.
const LEGACY_TIER_RATE_BOUNDS: Record<NormalizedTier, RateBounds> = {
  resident: { min: 45, max: 105 },
  associate: { min: 115, max: 200 },
};

export function getRateBounds(tier: RawTier): RateBounds {
  return TIER_RATE_BOUNDS[normalizeTier(tier)];
}

export interface DetermineSessionRateInput {
  quizRate?: number;
  therapistType?: RawTier;
  therapistTier?: RawTier;
}

/**
 * Resolve the per-session rate to stamp on a new episode/session.
 * Prefers an explicit quiz-picked rate; otherwise falls back to the
 * tier's minimum (the lowest sliding-scale commitment, matching the
 * historical `determineSessionRate` behavior in apps/client).
 */
export function determineSessionRate(input: DetermineSessionRateInput): number {
  if (input.quizRate) return input.quizRate;
  return getRateBounds(input.therapistType ?? input.therapistTier).min;
}

export interface ValidateRateResult {
  valid: boolean;
  error?: string;
}

export interface ValidateRateOptions {
  // Accept rates valid under the previous policy. Pass true when the rate is
  // being reused from an existing episode or a pre-change quiz answer, so
  // returning clients aren't locked out of their grandfathered rate.
  allowLegacy?: boolean;
}

export function validateSessionRate(
  rate: number,
  therapistTier: RawTier,
  options: ValidateRateOptions = {},
): ValidateRateResult {
  const tier = normalizeTier(therapistTier);
  const bounds = TIER_RATE_BOUNDS[tier];
  const label = tier === 'resident' ? 'Intern' : 'Associate';
  if (rate >= bounds.min && rate <= bounds.max) {
    return { valid: true };
  }
  if (options.allowLegacy) {
    const legacy = LEGACY_TIER_RATE_BOUNDS[tier];
    if (rate >= legacy.min && rate <= legacy.max) {
      return { valid: true };
    }
  }
  // Keep the error neutral — this validator is called from the self-pay
  // picker (where "retake the quiz" is nonsensical, there's no quiz),
  // the booking confirm flow, and the admin surfaces. Say what range is
  // valid, don't instruct a specific recovery path (the UI around the
  // picker can suggest "pick a chip" if it wants).
  const base = `For ${label} therapists, the session rate must be between $${bounds.min} and $${bounds.max}.`
  return {
    valid: false,
    error: rate > 0 ? `${base} Your current rate is $${rate}.` : base,
  };
}

/**
 * Build a short set of evenly-spaced rate "chips" for a picker UI.
 * Rounds intermediate values to the nearest $5 for readable buttons;
 * always pins the first/last chip to the exact bounds so the min and
 * max are selectable even when they're off the $5 grid. Dedup + sort
 * are last so the output is monotonic.
 */
export function buildRateChips(min: number, max: number): number[] {
  const steps = 6;
  const raw = Array.from({ length: steps + 1 }, (_, i) => min + ((max - min) * i) / steps);
  const rounded = raw.map((n) => Math.round(n / 5) * 5);
  rounded[0] = min;
  rounded[rounded.length - 1] = max;
  return Array.from(new Set(rounded)).sort((a, b) => a - b);
}
